import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { ruleAPI } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Scale, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const MyRules = () => {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await ruleAPI.listAll();
      setRules(res.data);
    } catch (err) {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const getRuleTypeColor = (type) => {
    switch (type) {
      case 'SPEND_LIMIT': return { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/20' };
      case 'RECEIVE_LIMIT': return { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/20' };
      case 'ALLOCATION': return { bg: 'bg-blue-900/10', text: 'text-blue-600', border: 'border-blue-600/20' };
      default: return { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/20' };
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="my-rules-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">My Rules & Limits</h1>
          <p className="text-gray-500 mt-1">View spending limits and allocations for your department</p>
        </div>

        {rules.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {rules.map((rule) => {
              const utilization = rule.amount > 0 ? (rule.current_usage / rule.amount) * 100 : 0;
              const isApproaching = utilization >= 80;
              const colors = getRuleTypeColor(rule.rule_type);
              
              return (
                <Card key={rule.rule_id} className={`glass-card border ${colors.border}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <div className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text} mb-2`}>
                          {rule.rule_type.replace('_', ' ')}
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">{rule.period || 'Total'} Limit</h3>
                        {rule.description && <p className="text-sm text-gray-500 mt-1">{rule.description}</p>}
                      </div>
                      {isApproaching ? (
                        <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-900/10 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-500">Used</span>
                          <span className="text-gray-900 font-['JetBrains_Mono']">
                            {formatCurrency(rule.current_usage)} / {formatCurrency(rule.amount)}
                          </span>
                        </div>
                        <Progress 
                          value={Math.min(utilization, 100)} 
                          className={`h-3 ${isApproaching ? 'bg-amber-500/20' : 'bg-gray-50'}`}
                        />
                        <p className="text-xs text-gray-500 mt-1 text-right">{utilization.toFixed(1)}% utilized</p>
                      </div>
                      
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Remaining Budget</span>
                          <span className={`text-xl font-bold font-['JetBrains_Mono'] ${isApproaching ? 'text-amber-500' : 'text-blue-600'}`}>
                            {formatCurrency(rule.remaining_budget)}
                          </span>
                        </div>
                        {isApproaching && (
                          <p className="text-xs text-amber-500 mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            You're approaching your limit. Plan your spending accordingly.
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Scale className="w-16 h-16 text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Rules Configured</h2>
              <p className="text-gray-500 text-center max-w-md">
                Your administrator hasn't set any spending limits or allocations for your department yet. 
                You can initiate payments without restrictions.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="glass-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Understanding Your Limits</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-amber-500/5 rounded-lg border border-amber-500/10">
                <h4 className="text-amber-500 font-medium mb-2">Spend Limit</h4>
                <p className="text-sm text-gray-500">Maximum amount you can spend within a specific time period (daily, weekly, monthly, or yearly).</p>
              </div>
              <div className="p-4 bg-blue-500/5 rounded-lg border border-blue-500/10">
                <h4 className="text-blue-500 font-medium mb-2">Receive Limit</h4>
                <p className="text-sm text-gray-500">Maximum amount your department can receive from external parties within a period.</p>
              </div>
              <div className="p-4 bg-blue-900/5 rounded-lg border border-blue-600/10">
                <h4 className="text-blue-600 font-medium mb-2">Fund Allocation</h4>
                <p className="text-sm text-gray-500">Total budget allocated to your department. Once used, you'll need additional allocation.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default MyRules;
